import { Check, ChevronDown } from "lucide-react";
import { useInput, useTranslate } from "ra-core";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import {
  LIST_FILTER_CODECS,
  toggleFilterValue,
  type ListFilterMode,
} from "./listFilterValue";

export interface FilterChoice {
  value: string;
  label: string;
  color?: string;
}

export interface ChoiceFilterProps {
  source: string;
  /** Shown on the button and in the « Ajouter un filtre » menu. */
  label: string;
  choices: FilterChoice[];
  mode?: ListFilterMode;
  /** A choice that cannot be combined with the others, like « À définir ». */
  exclusiveValue?: string;
  alwaysOn?: boolean;
}

/** Above this many choices, the list gets a search box. */
const SEARCH_THRESHOLD = 8;

/**
 * Filter of the list's top bar: a button opening the choices as checkboxes,
 * so several values can be picked at once (status Prospect or Client).
 */
export const ChoiceFilter = ({
  source,
  label,
  choices,
  mode = "in",
  exclusiveValue,
}: ChoiceFilterProps) => {
  const translate = useTranslate();
  const { field } = useInput({ source });
  const [isOpen, setOpen] = useState(false);
  const codec = LIST_FILTER_CODECS[mode];
  const selected = codec.read(field.value);
  const selectedLabels = choices
    .filter((choice) => selected.includes(choice.value))
    .map((choice) => choice.label);
  const title = translate(label, { _: label });

  const toggle = (value: string) => {
    const next = toggleFilterValue(selected, value, {
      isSingle: mode === "single",
      exclusiveValue,
    });
    field.onChange(codec.write(next) ?? "");
  };

  return (
    <Popover open={isOpen} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn(
            "h-9 max-w-72 gap-1.5 font-normal",
            selected.length > 0 && "border-primary/50 bg-primary/5",
          )}
        >
          <span className="font-medium">{title}</span>
          {selectedLabels.length > 0 ? (
            <span className="truncate text-muted-foreground">
              {selectedLabels[0]}
              {selectedLabels.length > 1
                ? ` +${selectedLabels.length - 1}`
                : ""}
            </span>
          ) : null}
          <ChevronDown className="size-4 shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0">
        <Command>
          {choices.length > SEARCH_THRESHOLD ? (
            <CommandInput placeholder={translate("crm.filters.search")} />
          ) : null}
          <CommandList>
            <CommandEmpty>{translate("crm.filters.no_match")}</CommandEmpty>
            <CommandGroup>
              {choices.map((choice) => {
                const isSelected = selected.includes(choice.value);
                return (
                  <CommandItem
                    key={choice.value}
                    value={`${choice.label} ${choice.value}`}
                    onSelect={() => toggle(choice.value)}
                    aria-checked={isSelected}
                  >
                    <span
                      className={cn(
                        "flex size-4 shrink-0 items-center justify-center border",
                        mode === "single" ? "rounded-full" : "rounded-sm",
                        isSelected
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-input",
                      )}
                    >
                      {isSelected ? <Check className="size-3" /> : null}
                    </span>
                    {choice.color ? (
                      <span
                        className="size-2 shrink-0 rounded-full"
                        style={{ backgroundColor: choice.color }}
                      />
                    ) : null}
                    <span className="truncate">{choice.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
          {selected.length > 0 ? (
            <div className="border-t p-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="w-full"
                onClick={() => field.onChange("")}
              >
                {translate("crm.filters.clear")}
              </Button>
            </div>
          ) : null}
        </Command>
      </PopoverContent>
    </Popover>
  );
};
